/* eslint-disable camelcase */
import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Loading from "../../components/Misc/Loading";
import { getUser } from "@nualang/nualang-api-and-queries/APIs/Users";
import { users as userQuerys } from "@nualang/nualang-api-and-queries/Queries";
import { createCheckoutSession } from "@nualang/nualang-api-and-queries/APIs/Payments";
import { getClassrooms } from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { classrooms as classroomHelpers } from "../../utils/helpers";
import { classrooms as classroomQuerys } from "@nualang/nualang-api-and-queries/Queries";
import plans from "../../plans/index";
import config from "../../config";
import logoPrimary from "../../img/logo/nualang-logo-primary.svg";
import paymentErrorImage from "../../img/stripe-error-image.svg";
import { searchStringToObj } from "../../utils";
import logger from "../../logger";
import useCreateFirstClassroom from "../../hooks/useCreateFirstClassroom";

const Logo = styled("img")(() => ({
  height: 50,
}));

const PaymentErrorImage = styled("img")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "block",
    maxHeight: "200px",
  },
  display: "block",
  maxHeight: "400px",
  margin: "auto",
}));

function Payment(props) {
  const {
    location,
    authData: { username, attributes },
    navigate,
  } = props;
  const searchParams = searchStringToObj(location.search);
  const {
    plan,
    interval,
    currency,
    redirect,
    success,
    canceled,
    session_id,
    promotion_code,
    coupon,
    students,
    billingCycleAnchor,
  } = searchParams;
  const [errorMessage, setErrorMessage] = useState(null);
  let selectedPlan;
  if (plan) {
    selectedPlan = plans[plan].versions[students || 50];
  }

  const { createFirstClassroom } = useCreateFirstClassroom({
    location,
    navigate,
    attributes,
  });

  const userQuery = userQuerys.useUser(
    async (userId) => {
      const response = await getUser(userId, false);
      return response;
    },
    {
      userId: username,
    },
    {
      enabled: !!username,
    },
  );

  const currentUser =
    userQuery.isSuccess && userQuery.data ? userQuery.data : {};

  const teachClassroomsQuery = classroomQuerys.useClassrooms(
    async (filters) => {
      const classrooms = await getClassrooms(filters);
      const formattedClassrooms = await classroomHelpers.formatClassrooms(
        classrooms.Items,
        false,
        props.authData,
      );
      return formattedClassrooms;
    },
    {
      filters: {
        createdBy: username,
        verbose: false,
        // Get all using high limit
        limit: config.limits.teachClassrooms,
      },
    },
    { enabled: !!username && !!success },
  );

  const handleCheckoutSession = useCallback(
    async (priceId) => {
      try {
        const sessionParams = {
          priceId,
          domainURL: window.location.origin,
          customerId: currentUser.stripeCustomerId,
          customerEmail: currentUser.UserAttributes.email,
          redirectURL: redirect,
          promotion_code,
          studentLimit: students || null,
          billingCycleAnchor: billingCycleAnchor || null,
          /* 
            Apply default coupon for Teacher PRO 200
            This is the only plan available right now and the coupon is applied in all cases
          */
          coupon: coupon || process.env.REACT_APP_STRIPE_PRICE_COUPON,
        };
        // Only allow free trial for new customers and non promotions
        if (!currentUser.stripeCustomerId && !promotion_code && !coupon) {
          sessionParams.trialPeriodDays = selectedPlan.trialPeriodDays;
        }
        const response = await createCheckoutSession(sessionParams);
        const { session } = response;
        if (session && session.url) {
          window.location.replace(session.url);
        }
      } catch (error) {
        logger.error(error);
        setErrorMessage("There was a problem loading checkout");
      }
    },
    [
      coupon,
      currentUser?.UserAttributes?.email,
      currentUser?.stripeCustomerId,
      promotion_code,
      redirect,
      selectedPlan?.trialPeriodDays,
    ],
  );

  const handlePaymentSuccess = useCallback(async () => {
    try {
      const teachClassrooms =
        teachClassroomsQuery.isSuccess &&
        Array.isArray(teachClassroomsQuery.data) &&
        teachClassroomsQuery.data.length
          ? teachClassroomsQuery.data
          : [];
      if (teachClassrooms && teachClassrooms.length > 0) {
        // Classrooms already created
        if (redirect) {
          window.location.replace(redirect);
        } else {
          navigate("/classrooms");
        }
      } else {
        // Create the first classroom
        createFirstClassroom();
      }
    } catch (error) {
      logger.error(error);
      navigate("/classrooms");
    }
  }, [navigate, redirect, session_id, teachClassroomsQuery.isSuccess]);

  const handlePaymentCanceled = async () => {
    try {
      window.location.href = `${config.SITE_URL}/pricing/app`;
    } catch (error) {
      logger.error(error);
    }
  };

  useEffect(() => {
    if (success && teachClassroomsQuery.isSuccess) {
      handlePaymentSuccess();
    } else if (canceled) {
      handlePaymentCanceled();
    } else if (
      interval &&
      currency &&
      selectedPlan &&
      selectedPlan.billingPeriods &&
      selectedPlan.billingPeriods[interval] &&
      !userQuery.isLoading
    ) {
      handleCheckoutSession(
        selectedPlan.billingPeriods[interval][currency].priceId,
      );
    }
  }, [
    userQuery.isLoading,
    teachClassroomsQuery.isSuccess,
    canceled,
    currency,
    interval,
    selectedPlan,
    success,
    handleCheckoutSession,
    handlePaymentSuccess,
  ]);

  // if (message) {
  //   return <h1 style={{padding: 64}}>{message}</h1>
  // }

  return (
    <Dialog fullScreen open={true}>
      {errorMessage ? (
        <Grid
          container
          direction="column"
          spacing={3}
          sx={{
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            p: 2
          }}>
          <Grid>
            <Link to="/" reloadDocument>
              <Logo src={logoPrimary} />
            </Link>
          </Grid>
          <Grid>
            <Link to="/" reloadDocument>
              <PaymentErrorImage src={paymentErrorImage} alt="Error" />
            </Link>
          </Grid>
          <Grid>
            <Box sx={{
              textAlign: "center"
            }}>
              {errorMessage}, please{" "}
              <a href={`${config.SITE_URL}/contact`}>contact us</a> for
              assistance.
            </Box>
          </Grid>
          <Grid>
            <Button component={Link} to="/" variant="outlined" reloadDocument>
              Back to App
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Loading />
      )}
    </Dialog>
  );
}

Payment.propTypes = {
  authData: PropTypes.object,
};

export default Payment;
